import { cn } from '~/lib/utils';

export const typographyVariants = {
  title: 'text-foreground text-2xl font-semibold tracking-tight',
  heading: 'text-foreground text-base font-semibold tracking-tight',
  section: 'text-foreground text-sm font-semibold tracking-tight',
  body: 'text-foreground text-sm leading-5',
  muted: 'text-muted-foreground text-sm leading-5',
  caption: 'text-muted-foreground text-[11px] leading-snug',
  label: 'text-muted-foreground text-[11px] font-medium uppercase tracking-[0.08em]',
  cardTitle: 'text-foreground text-[13px] font-medium tracking-tight',
} as const;

export type TypographyVariant = keyof typeof typographyVariants;

const defaultTags: Record<TypographyVariant, 'h1' | 'h2' | 'h3' | 'p' | 'span'> = {
  title: 'h1',
  heading: 'h2',
  section: 'h3',
  body: 'p',
  muted: 'p',
  caption: 'p',
  label: 'span',
  cardTitle: 'h3',
};

export type TypographyProps = {
  variant?: TypographyVariant;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div' | 'label';
  className?: string;
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'>;

/**
 * Operate type roles from DESIGN.md — system UI, tight tracking on titles,
 * 11px muted labels/captions. Use instead of one-off text-* stacks.
 */
export default function Typography({
  variant = 'body',
  as,
  className,
  children,
  ...props
}: TypographyProps) {
  const Tag = as ?? defaultTags[variant];
  return (
    <Tag className={cn(typographyVariants[variant], className)} {...props}>
      {children}
    </Tag>
  );
}

export { typographyVariants as typography };
